import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input, OnChanges, SimpleChanges, SimpleChange } from '@angular/core';
import { Router } from '@angular/router';
import {SalesRepDialogComponent} from '../dialogs/sales-rep-dialog/sales-rep-dialog.component';
import {Entity} from '../entity/entity';
import {Opportunity} from './opportunity';
import {PersonLogin} from '../person/person-login';
import {SelectionItem} from '../selections/selection-item';
import {OpportunityService} from '../services/opportunity-service';
import {PersonLoginService} from '../services/person-login-service';
import {PersonService} from '../services/person-service';
import {SelectionService} from '../services/selection-service';
import {DateUtils} from '../utils/date-utils';
import {StringUtils} from '../utils/string-utils';
import {AssociatedQuotesDialogComponent} from './associated-quotes-dialog.component';
import {AwardProbability} from './award-probability';
import {MatDialog} from '@angular/material/dialog';
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from '@angular/material/datepicker';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatMenu, MatMenuItem, MatMenuTrigger} from '@angular/material/menu';
import {MatIcon} from '@angular/material/icon';
import {MatButton} from '@angular/material/button';
import { CurrencyPipe, DatePipe, DecimalPipe, NgClass } from '@angular/common';
import {FormsModule} from '@angular/forms';

@Component({
    selector: 'opportunity-detail',
    templateUrl: './opportunity-detail.component.html',
    styleUrls: ['./opportunity-detail.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, MatButton, MatIcon, MatMenuTrigger, MatMenu, MatMenuItem, MatProgressSpinner, NgClass, MatDatepickerInput, MatDatepickerToggle, MatDatepicker, DecimalPipe, CurrencyPipe, DatePipe]
})
export class OpportunityDetailComponent implements OnChanges, OnInit {
  private readonly personLoginService = inject(PersonLoginService);
  private readonly opportunityService = inject(OpportunityService);
  private readonly selectionService = inject(SelectionService);
  private readonly personService = inject(PersonService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  readonly opportunityId = input.required<string>({ alias: 'opportunity-id' });
  readonly opportunityRevision = input.required<string>({ alias: 'opportunity-revision' });


  private personLogin!: PersonLogin;
  public readonly isBusy = signal<boolean>(undefined!);
  public readonly errorMessage = signal<string>(undefined!);
  public readonly opportunity = signal<Opportunity>(undefined!);
  public readonly associatedQuote = signal<Entity>(undefined!);
  public readonly associatedQuotes = signal<Entity[]>(undefined!);
  public readonly statuses = signal<SelectionItem[]>([]);
  public readonly phases = signal<SelectionItem[]>([]);
  public readonly competitors = signal<SelectionItem[]>([]);
  public readonly wonLossReasons = signal<SelectionItem[]>([]);
  private readonly awardProbabilities = signal<AwardProbability[]>([]);
  readonly marketSegmentDescription = signal<string>(undefined!);
  readonly projectTypeDescription = signal<string>(undefined!);
  readonly salesChannelDescription = signal<string>(undefined!);
  readonly leadTimeDescription = signal<string>(undefined!);
  readonly specTypeDescription = signal<string>(undefined!);
  readonly applicationTypeDescription = signal<string>(undefined!);

  constructor
  () {
    this.associatedQuote.set(new Entity());
  }

  ngOnInit(): void {
    this.personLogin = this.personLoginService.getCachedLogin()!;
    if (!this.personLogin) {
      this.router.navigate(['main']).then(() => this.isBusy.set(false));
    } else if (this.personLoginService.isOutsideEngineer()) {
      this.router.navigate(['ratings']).then(() => this.isBusy.set(false));
    } else {
      this.isBusy.set(true);
      this.initialize();
    }
  }

  private getOpportunity(id: string, revision: string): void {
    this.opportunityService.getOpportunity(id, +revision).subscribe({
      next: result => {
        this.opportunity.set(result);
        this.opportunityService.getAssociatedQuotes(id, +revision).subscribe(quotes => this.associatedQuotes.set(quotes));
        if (result && result.associatedQuoteId && result.associatedQuoteId.length > 0) {
          let quoteId = this.opportunity().associatedQuoteId.substring(0, 10);
          let quoteRevision = this.opportunity().associatedQuoteId.substring(11);
          this.opportunityService.getAssociatedQuote(quoteId, +quoteRevision).subscribe(quote => {
            this.associatedQuote.set(quote);
            this.loadMarketSegmentDescription();
            this.loadProjectTypeDescription();
            this.loadSalesChannelDescription();
            this.loadLeadTimeDescription();
            this.loadSpecTypeDescription();
            this.loadApplicationTypeDescription();
            this.opportunityService.getAllAwardProbabilities(this.associatedQuote().brandID).subscribe(awards => this.awardProbabilities.set(awards));
            this.selectionService.getFpaCompetitors(this.associatedQuote().brandID).subscribe(competitor => this.competitors.set(competitor));
          });
        }
        this.loadSelectionLists();
        this.isBusy.set(false);
      }, error: error => this.handleError(error)
    });
  }

  public saveOpportunity(): void {
    if (!this.personLogin || this.personLoginService.isOutsideEngineer()) {
      this.router.navigate(['main']).then(() => this.isBusy.set(false));
    } else {
      this.isBusy.set(true);
      this.opportunityService.save(this.opportunity()).subscribe({
        next: result => {
          this.opportunity.set(result);
          this.isBusy.set(false);
        }, error: error => this.handleError(error)
      });
    }
  }

  private loadSelectionLists(): void {
    this.selectionService.getOpportunityStatusCodes().subscribe(status => this.statuses.set(status));
    this.selectionService.getOpportunityPhaseCodes().subscribe(phase => this.phases.set(phase));
    this.selectionService.getAllIecWinLossReasons().subscribe(reasons => this.wonLossReasons.set(reasons));
  }

  private loadMarketSegmentDescription(): void {
    this.selectionService.getMarketSegments().subscribe(results => {
      const filtered = results.find(marketSegment => marketSegment.code === this.associatedQuote().marketSegmentCode);
      if (filtered) {
        this.marketSegmentDescription.set(filtered.description);
      }
    });
  }

  private loadProjectTypeDescription(): void {
    this.selectionService.getProjectTypes().subscribe(results => {
      const filtered = results.find(projectType => projectType.code === this.associatedQuote().projectTypeCode);
      if (filtered) {
        this.projectTypeDescription.set(filtered.description);
      }
    });
  }

  private loadSalesChannelDescription(): void {
    this.selectionService.getSalesChannels().subscribe(results => {
      const filtered = results.find(channel => channel.code === this.associatedQuote().salesChannelCode);
      if (filtered) {
        this.salesChannelDescription.set(filtered.description);
      }
    });
  }

  private loadLeadTimeDescription(): void {
    this.selectionService.getPriorities().subscribe(results => {
      const filtered = results.find(leadTime => leadTime.code === this.associatedQuote().leadTimeCode);
      if (filtered) {
        this.leadTimeDescription.set(filtered.description);
      }
    });
  }

  private loadSpecTypeDescription(): void {
    this.specTypeDescription.set('');
    this.selectionService.getNewExtendCodes().subscribe({
      next: (results: SelectionItem[]) => {
        const filtered = results.filter((code: SelectionItem) => code.code === this.associatedQuote().specTypeCode);
        if ((filtered?.length || 0) > 0) {
          this.specTypeDescription.set(filtered[0].description);
        }
      }
    });
  }

  private loadApplicationTypeDescription(): void {
    this.applicationTypeDescription.set('');
    this.selectionService.getApplicationTypes().subscribe({
      next: (results: SelectionItem[]) => {
        const filtered = results.filter((code: SelectionItem) => +code.code === this.associatedQuote().applicationTypeCode);
        if ((filtered?.length || 0) > 0) {
          this.applicationTypeDescription.set(filtered[0].description);
        }
      }
    });
  }

  public refreshAwardProbabilityPercent(currentPhaseCode: string): void {
    this.opportunity().awardProbabilityPercent = this.awardProbabilities().find(
      awardProbability => awardProbability.statusCode === currentPhaseCode)!.awardProbabilityPercent;
  }

  public showOutsideSalesRep(): void {
    if (this.associatedQuote().outsideSalesRepId && this.associatedQuote().outsideSalesRepId.length > 0) {
      this.personService.getContactPerson(this.associatedQuote().outsideSalesRepId).subscribe(result => {
        let outsideRep = result;
        if (outsideRep) {
          this.dialog.open(SalesRepDialogComponent, {
            disableClose: true,
            width: '480px',
            height: '170px',
            data: {rep: outsideRep},
          });
        }
      });
    }
  }

  public showSalesRep(): void {
    if (this.associatedQuote().salesRepId && this.associatedQuote().salesRepId.length > 0) {
      this.personService.getContactPerson(this.associatedQuote().salesRepId).subscribe(result => {
        let salesRep = result;
        if (salesRep) {
          this.dialog.open(SalesRepDialogComponent, {
            disableClose: true,
            width: '480px',
            height: '170px',
            data: {rep: salesRep},
          });
        }
      });
    }
  }

  public viewQuotes(): void {
    if (this.associatedQuotes() && this.associatedQuotes().length > 0) {
      this.dialog.open(AssociatedQuotesDialogComponent, {
        disableClose: true,
        width: '1360px',
        height: '440px',
        data: {quotes: this.associatedQuotes(), opportunity: this.opportunity()},
      });
    }
  }

  public formatDisplayDate(input: Date): Date {
    return DateUtils.formatDisplay(input);
  }

  public setExpectedBookDate(newValue: Date) {
    this.opportunity().expectedBookDate = DateUtils.formatOutput(newValue);
  }

  public setExpectedFirstShipmentDate(newValue: Date) {
    this.opportunity().expectedFirstShipmentDate = DateUtils.formatOutput(newValue);
  }

  public backToOpportunityMaster(): void {
    this.router.navigate(['opportunities']).then(() => this.isBusy.set(false));
  }

  private handleError(error: any): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  dateStartValue(input: Date): Date {
    if (!input) {
      return new Date();
    } else {
      const newDate = new Date(input);
      const yearValue = newDate.getFullYear();
      if (isNaN(yearValue) || yearValue < 2000) {
        return new Date();
      } else {
        return new Date(input);
      }
    }
  }


  private initialize(): void {
    let id: string = this.opportunityId();
    let revision: string = this.opportunityRevision();
    this.getOpportunity(id, revision);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.values(changes).some((change: SimpleChange) => !change.firstChange)) {
      this.initialize();
    }
  }
}
